import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AchievementBadge } from "./AchievementBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Users, MessageSquare, Briefcase, Star } from "lucide-react";

interface Achievement {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  badge_color: string;
  requirement_count: number;
}

interface UserAchievement {
  achievement_id: string;
  progress: number;
  unlocked_at: string | null;
}

export function AchievementsGrid() {
  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: achievements, isLoading: loadingAchievements } = useQuery({
    queryKey: ["achievements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("achievements")
        .select("*")
        .order("points", { ascending: true });

      if (error) throw error;
      return data as Achievement[];
    },
  });

  const { data: userAchievements, isLoading: loadingUserAchievements } = useQuery({
    queryKey: ["user-achievements", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_achievements")
        .select("achievement_id, progress, unlocked_at")
        .eq("user_id", user.id);

      if (error) throw error;
      return data as UserAchievement[];
    },
    enabled: !!user,
  });

  if (loadingAchievements || loadingUserAchievements) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (!achievements) return null;

  const categories = {
    community: { label: "Comunidade", icon: MessageSquare },
    profile: { label: "Perfil", icon: Users },
    engagement: { label: "Engajamento", icon: Star },
    jobs: { label: "Vagas", icon: Briefcase },
    milestone: { label: "Marcos", icon: Trophy },
  };

  const groupedAchievements = achievements.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = [];
    }
    acc[achievement.category].push(achievement);
    return acc;
  }, {} as Record<string, Achievement[]>);

  const unlockedCount = userAchievements?.filter(ua => ua.unlocked_at).length || 0;
  const totalPoints = userAchievements?.reduce((sum, ua) => {
    const achievement = achievements.find(a => a.id === ua.achievement_id);
    return ua.unlocked_at && achievement ? sum + achievement.points : sum;
  }, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header com estatísticas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Suas Conquistas
          </CardTitle>
          <CardDescription>
            {unlockedCount} de {achievements.length} conquistas desbloqueadas • {totalPoints} pontos totais
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Grid de conquistas por categoria */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="w-full justify-start flex-wrap h-auto">
          <TabsTrigger value="all">Todas ({achievements.length})</TabsTrigger>
          {Object.entries(categories).map(([key, { label, icon: Icon }]) => {
            const count = groupedAchievements[key]?.length || 0;
            return (
              <TabsTrigger key={key} value={key} className="gap-2">
                <Icon className="h-3 w-3" />
                {label} ({count})
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((achievement) => {
              const userAchievement = userAchievements?.find(
                (ua) => ua.achievement_id === achievement.id
              );
              const isUnlocked = !!userAchievement?.unlocked_at;
              const progress = userAchievement?.progress || 0;

              return (
                <AchievementBadge
                  key={achievement.id}
                  achievement={achievement}
                  isUnlocked={isUnlocked}
                  progress={progress}
                  showProgress={!isUnlocked}
                />
              );
            })}
          </div>
        </TabsContent>

        {Object.entries(groupedAchievements).map(([category, categoryAchievements]) => (
          <TabsContent key={category} value={category} className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryAchievements.map((achievement) => {
                const userAchievement = userAchievements?.find(
                  (ua) => ua.achievement_id === achievement.id
                );
                const isUnlocked = !!userAchievement?.unlocked_at;
                const progress = userAchievement?.progress || 0;

                return (
                  <AchievementBadge
                    key={achievement.id}
                    achievement={achievement}
                    isUnlocked={isUnlocked}
                    progress={progress}
                    showProgress={!isUnlocked}
                  />
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
